import { Button, DatePicker, Input, Select, TableProps } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { capitalize } from "lodash";
import { useEffect, useState } from "react";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import CustomTable from "../../../components/ui/CustomTable";
import {
  useGetAllStatementsQuery,
  useLazyGetBankStatementsDownloadQuery,
  useLazyGetDownloadStatementsQuery,
} from "../../../features/portal/statements/statementsApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { formatDate } from "../../../lib/time";
import { toast } from "../../../lib/toast";
import {
  GetAllStatementsDataParamsType,
  GetAllStatementsDataType,
} from "../../../types/portal/statements";
import { countParPageSerialNumber } from "../../../utils/number";
import { addKeyInArray } from "../../../utils/utils";
import SelectStatementModal from "./_components/SelectStatementModal";

export default function Statements() {
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const {
    open: openSelectStatementModal,
    handleOpen: handleSelectStatementOpen,
    handleClose: handleSelectStatementClose,
  } = useModal<undefined, "SELECT_STATEMENT">();

  const {
    page,
    limit,
    handlePagination,
    handleFilter,
    handleLimit,
    filter,
    keyword,
    handleSearch,
  } = useTable<GetAllStatementsDataParamsType>();

  const { RangePicker } = DatePicker;

  const { data, isError, error, isLoading, refetch } = useGetAllStatementsQuery(
    {
      params: {
        ...filter,
        page: page,
        limit: limit,
        msisdn: keyword,
        fromDate: filter.fromDate,
        toDate: filter.toDate,
        skip: undefined,
      },
    }
  );
  const [
    triggerDownload,
    {
      isSuccess: getDownloadStatementsIsSuccess,
      isLoading: getDownloadStatementsIsLoading,
    },
  ] = useLazyGetDownloadStatementsQuery();

  const [
    triggerStatementDownload,
    { data: bankStatementData, isLoading: getDownloadBankStatementsIsLoading },
  ] = useLazyGetBankStatementsDownloadQuery();
  useError(isError, error, "Get all statements data fail");

  const columns: TableProps<GetAllStatementsDataType>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: <span className="whitespace-nowrap">Request Date</span>,
      dataIndex: "created_at",
      render: (val: string) => (val ? <>{formatDate(val)}</> : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Wallet Number</span>,
      align: "center",
      dataIndex: "msisdn",
      render: (val) => (val ? val : "N/A"),
    },
    {
      title: <span className="whitespace-nowrap">Statement Date Range</span>,
      align: "center",
      render: (val) =>
        val ? (
          <>
            {formatDate(val?.startDate)} - {formatDate(val?.endDate)}
          </>
        ) : (
          "N/A"
        ),
    },
    {
      title: <span className="whitespace-nowrap">Statement Period</span>,
      align: "center",
      render: (val) => {
        if (!val?.startDate || !val?.endDate) return "N/A";

        const start = dayjs(val.startDate);
        const end = dayjs(val.endDate);

        const diffDays = end.diff(start, "day") + 1;

        return val?.custom === false
          ? `Last ${diffDays} Days`
          : `${diffDays} Days`;
      },
    },

    {
      title: <div className="whitespace-nowrap ">Status</div>,
      dataIndex: "status",
      align: "center",
      render: (val: "pending" | "completed" | "failed" | string) => {
        const statusClasses: Record<string, string> = {
          pending: "bg-yellow-100 text-yellow-700",
          partiallySettled: "bg-yellow-100 text-yellow-700",
          completed: "bg-green-100 text-green-700",
          failed: "bg-red-100 text-red-700",
        };

        return (
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${
              statusClasses[val] || "bg-yellow-100 text-yellow-700"
            }`}
          >
            {capitalize(val)}
          </span>
        );
      },
    },
    {
      title: <span className="whitespace-nowrap">Reason</span>,
      align: "center",
      dataIndex: "error_message",
      render: (val) => (val ? val : ""),
    },
    {
      title: "Actions",
      align: "center",
      render: (val) => (
        <div className="flex justify-center items-center gap-2">
          {!val?.signedUrl ? (
            <Button
              type="text"
              onClick={() => {
                if (val?.status === "completed") {
                  handleDownload(val?.id);
                }
              }}
              className={`text-center justify-start text-sm font-medium px-2 py-1 rounded ${
                val?.status === "completed"
                  ? "text-[#5E718D] cursor-pointer hover:underline"
                  : "text-gray-400 cursor-not-allowed"
              }`}
              loading={
                (downloadingId === val?.id && getDownloadStatementsIsLoading) ||
                (downloadingId === val?.id && loading)
              }
            >
              PDF Generate
            </Button>
          ) : (
            <Button
              onClick={() => handleDownloadPdf(val?.id)}
              className="text-center justify-start text-blue-600 text-sm font-medium"
              loading={
                (downloadingId === val?.id &&
                  getDownloadBankStatementsIsLoading) ||
                (downloadingId === val?.id && loading)
              }
            >
              Download
            </Button>
          )}
        </div>
      ),
    },
  ];

  const handleDownload = (id: number) => {
    setDownloadingId(id);
    triggerDownload({ id });
  };

  const handleDownloadPdf = async (id: number) => {
    try {
      setDownloadingId(id);
      const response = await triggerStatementDownload({ id }).unwrap();

      const signedUrl =
        response?.data?.signedUrl || bankStatementData?.data?.signedUrl;

      if (signedUrl) {
        window.open(signedUrl, "_blank");
      } else {
        toast("error", "No PDF URL found.");
      }
    } catch (error) {
      console.error("PDF download error:", error);
      toast("error", "Failed to download PDF.");
    } finally {
      setDownloadingId(null);
    }
  };

  useEffect(() => {
    if (getDownloadStatementsIsSuccess) {
      setLoading(true);
      setTimeout(() => {
        toast("success", "PDF generated successfully!");
        refetch();
        setDownloadingId(null);
        setLoading(false);
      }, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getDownloadStatementsIsSuccess, refetch]);

  return (
    <>
      {openSelectStatementModal?.type === "SELECT_STATEMENT" && (
        <SelectStatementModal
          title=""
          subTitle=""
          buttonName="Send"
          open={openSelectStatementModal?.open}
          handleClose={handleSelectStatementClose}
          action
          buttonBgDeep
          loading={false}
        />
      )}

      <SidebarLayout title="Statements">
        <CustomTable
          loading={isLoading}
          limit={limit}
          page={page}
          columns={columns}
          total={data?.data?.pagination?.total}
          dataSource={addKeyInArray(data?.data?.data)}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          filterSection={
            <>
              <Input
                suffix={<SearchIcon />}
                placeholder="Search wallet"
                style={{ width: 205 }}
                allowClear
                onChange={handleSearch}
              />

              <Select
                placeholder="Status"
                showSearch
                options={[
                  { label: "Pending", value: "pending" },
                  { label: "Completed", value: "completed" },
                  { label: "Failed", value: "failed" },
                ]}
                value={filter?.status ?? undefined}
                onChange={(value) => handleFilter("status", value)}
                className="!min-w-[110px]"
                suffixIcon={<SelectDownIcon />}
                // style={{ width: "100%" }}
                allowClear
                optionFilterProp="label"
              />

              <RangePicker
                allowClear
                value={range}
                onChange={(dates) => {
                  setRange(dates);
                  if (dates && dates[0] && dates[1]) {
                    handleFilter("fromDate", dates[0].format("YYYY-MM-DD"));
                    handleFilter("toDate", dates[1].format("YYYY-MM-DD"));
                  } else {
                    handleFilter("fromDate", undefined);
                    handleFilter("toDate", undefined);
                  }
                }}
                // disabledDate={(current) =>
                //   current && current > dayjs().endOf("day")
                // }
              />

              <Button
                onClick={() =>
                  handleSelectStatementOpen({ type: "SELECT_STATEMENT" })
                }
                type="primary"
                className="px-3"
              >
                Request Statement
              </Button>
            </>
          }
        />
      </SidebarLayout>
    </>
  );
}
